/**
 * Sprite animation selection + frame math (#223 Task 4) — PURE, deterministic,
 * no Pixi, no sim import. The client CHOOSES an animation from snapshot-derived
 * state (never from sim/hash state) and computes which sheet cell to draw from a
 * render-side clock. All the debuggable edges (missing animation, facing→row,
 * frame wrap) live here so they are unit-tested without a GPU.
 *
 * See `championSprites.ts` for the sheet layout the rects index into.
 */

import type {
  SpriteAnimationName,
  SpriteAnimationBlock,
  SpriteSheetMeta,
} from "./championSprites.js";

/** Snapshot-derived inputs to the animation choice (all cosmetic, non-hashed). */
export interface EntityAnimState {
  /** Entity is alive (hp > 0). */
  readonly alive: boolean;
  /** Entity is mid-cast (`cast !== null`). */
  readonly casting: boolean;
  /** Entity moved since the previous rendered frame (position delta). */
  readonly moving: boolean;
  readonly attacking?: boolean;
}

/**
 * Choose the animation NAME from state, by priority:
 *   dead → `death` · casting → `cast` · moving → `walk` · else → `idle`.
 * A dead entity never "casts" or "walks", so death wins outright.
 */
export function chooseAnimation(s: EntityAnimState): SpriteAnimationName {
  if (!s.alive) return "death";
  if (s.attacking === true) return "attack";
  if (s.casting) return "cast";
  if (s.moving) return "walk";
  return "idle";
}

/**
 * The animation to actually draw: `chooseAnimation`, but if the sheet did not
 * bake that block, fall back to `idle` (always present). Keeps a sheet with a
 * partial animation set from ever indexing a missing block.
 */
export function resolveAnimation(meta: SpriteSheetMeta, s: EntityAnimState): SpriteAnimationName {
  const want = chooseAnimation(s);
  return meta.animations[want] !== undefined ? want : "idle";
}

/**
 * Row WITHIN a block for a facing sector. An animation block is `facings`
 * consecutive rows; sector `f` is row `f`. Wraps modulo `facings` so an
 * out-of-range facing can never index outside the block.
 */
export function facingToSpriteRow(facing: number, facings: number): number {
  return ((Math.trunc(facing) % facings) + facings) % facings;
}

/**
 * Frame index for a `frames`-long, `fps` animation at render-clock `clockMs`.
 * Loops. A single-frame (or non-positive fps) animation pins to frame 0.
 * Render-side only — this clock never feeds the sim.
 */
export function spriteFrameIndex(frames: number, fps: number, clockMs: number): number {
  if (frames <= 1 || fps <= 0) return 0;
  const elapsed = clockMs < 0 ? 0 : clockMs;
  return Math.floor((elapsed / 1000) * fps) % frames;
}

/** A resolved sprite frame: which animation, and the source rect in sheet px. */
export interface EntitySpriteFrame {
  readonly animation: SpriteAnimationName;
  readonly block: SpriteAnimationBlock;
  readonly row: number;
  readonly frameIndex: number;
  /** Source rectangle in the sheet (px). */
  readonly sx: number;
  readonly sy: number;
  readonly sw: number;
  readonly sh: number;
}

/**
 * Resolve the exact sheet cell to draw for an entity: pick the animation, the
 * facing row, and the current frame, then compute the source rectangle. The
 * one function the Pixi draw calls; everything above it is independently tested.
 */
export function entitySpriteFrame(
  meta: SpriteSheetMeta,
  state: EntityAnimState,
  facing: number,
  clockMs: number,
): EntitySpriteFrame {
  const animation = resolveAnimation(meta, state);
  // `idle` is schema-required, and `resolveAnimation` only ever returns a name
  // whose block exists, so this lookup is always defined.
  const block = meta.animations[animation] ?? meta.animations.idle;
  const row = block.baseRow + facingToSpriteRow(facing, meta.facings);
  const frameIndex = spriteFrameIndex(block.frames, block.fps, clockMs);
  return {
    animation,
    block,
    row,
    frameIndex,
    sx: frameIndex * meta.frameWidth,
    sy: row * meta.frameHeight,
    sw: meta.frameWidth,
    sh: meta.frameHeight,
  };
}
